import { ErrorsContextProvider, ValidationErrors } from 'components';
import { Button, Header, Icon, Loader, Modal, ModalContent, Segment } from 'semantic-ui-react';
import { useManageExaminersToRoomStructure } from './useManageExaminersToRoomStructure';
import { ROUTING_PATHS } from 'const/routing-paths';
import { useIntParam, useSafeMutation } from 'hooks';
import { generatePath, useNavigate, Link } from 'react-router';
import {
  ExaminerSearchFiltersInitialState,
  ManageExaminerToRoomStructureFilterBar,
} from './ManageExaminerToRoomStructureFilterBar';
import { useCallback, useMemo, useState } from 'react';
import { ManageExaminerToRoomStructureSearchResults } from './ManageExaminerToRoomStructureSearchResults';
import styled from '@emotion/styled';
import { speakingSessionRoomStructureStore } from 'store';
import { parseServerErrors } from 'utils';
import { ExaminerAllocationChangedEmailNotificationMessage } from './EmailNotificationMessage';

const hourAndMinutesFormat = (time?: string | null): string => {
  if (!time) return '';
  const timeFormatRegex = /^(\d{2}:\d{2}):\d{2}$/;
  const match = timeFormatRegex.exec(time);
  return match ? match[1] : time;
};

export const ManageExaminerToRoomStructure = () => {
  const sessionId = useIntParam('sessionId');
  const [assignExaminer, { error: savingError, isLoading: isSaving }] =
    speakingSessionRoomStructureStore.service.speakingSessionRoomStructureApi.useAssignExaminerMutation();

  const { availableExaminers, isFetching, routeState } = useManageExaminersToRoomStructure();
  const { speakingSessionDetails } = ROUTING_PATHS;
  const errors = useMemo(() => parseServerErrors(savingError), [savingError]);

  const [filters, setFilters] = useState(ExaminerSearchFiltersInitialState);
  const [selectedExaminerId, setSelectedExaminerId] = useState<number | undefined>(
    routeState.assignedExaminerId
  );
  const [examinerChange, setExaminerChange] = useState<{
    oldExaminerName?: string;
    newExaminerName?: string;
  } | null>(null);

  const navigate = useNavigate();
  const goToSpeakingSessionDetails = useCallback(() => {
    navigate(generatePath(speakingSessionDetails.absolutePath, { sessionId }));
  }, [navigate, speakingSessionDetails.absolutePath, sessionId]);

  const { execute: assignExaminerToStructure } = useSafeMutation(assignExaminer, {
    successToastMessage: 'Examiner allocated successfully',
    onSuccess: goToSpeakingSessionDetails,
  });

  const examinerSearchResults = useMemo(() => {
    if (isFetching || !availableExaminers) return [];

    let filteredExaminers = availableExaminers;
    if (filters.examiner) {
      filteredExaminers = filteredExaminers.filter((examiner) => {
        const searchTerm = filters.examiner?.toLowerCase() || '';
        const searchWords = searchTerm
          .trim()
          .split(/\s+/)
          .filter((word) => word.length > 0);

        if (searchWords.length === 0) return true;

        return searchWords.some((word) => {
          return (
            examiner.firstName?.toLowerCase().includes(word) ||
            examiner.lastName?.toLowerCase().includes(word) ||
            examiner.examinerNumber?.toLowerCase().includes(word) ||
            examiner.email?.toLowerCase().includes(word)
          );
        });
      });
    }

    if (!filters.showExaminersFromOtherLocationsWithinThisCentre) {
      filteredExaminers = filteredExaminers.filter((examiner) => !!examiner.districtId);
    }

    if (filters.availability && filters.availability.length > 0) {
      filteredExaminers = filteredExaminers.filter((examiner) =>
        filters.availability?.includes(examiner.availability)
      );
    }

    return filteredExaminers;
  }, [availableExaminers, filters]);

  const handleExaminerSelect = useCallback(
    (examinerId: number) => {
      const newExaminer = availableExaminers?.find((e) => e.id === examinerId);
      const newExaminerName = newExaminer ? `${newExaminer.firstName} ${newExaminer.lastName}` : '';

      if (!routeState.assignedExaminerId) {
        setExaminerChange({ newExaminerName });
      } else if (routeState.assignedExaminerId !== examinerId) {
        const oldExaminer = availableExaminers?.find((e) => e.id === routeState.assignedExaminerId);
        const oldExaminerName = oldExaminer
          ? `${oldExaminer.firstName} ${oldExaminer.lastName}`
          : '';
        setExaminerChange({ oldExaminerName, newExaminerName });
      } else {
        setExaminerChange(null);
      }

      setSelectedExaminerId(examinerId);
    },
    [routeState.assignedExaminerId, availableExaminers]
  );

  return (
    <ErrorsContextProvider generalErrors={errors}>
      <Modal open size="tiny">
        <Header as="h3" css={{ padding: '1em' }}>
          Choose examiner
          <Header.Subheader css={{ marginTop: '5px' }}>
            <Icon name="clock outline" /> {hourAndMinutesFormat(routeState.structureStartTime)} -{' '}
            {hourAndMinutesFormat(routeState.structureEndTime)}
          </Header.Subheader>
        </Header>
        <ModalContent scrolling>
          <ValidationErrors />
          <S.FilterSticky>
            <ManageExaminerToRoomStructureFilterBar onFiltersChange={setFilters} />
          </S.FilterSticky>
          {isFetching ? (
            <Segment>
              <Loader active inline="centered" />
            </Segment>
          ) : (
            <ManageExaminerToRoomStructureSearchResults
              examiners={examinerSearchResults}
              onExaminerSelect={handleExaminerSelect}
              selectedExaminerId={selectedExaminerId}
            />
          )}
        </ModalContent>
        {examinerChange && (
          <S.Notification>
            <ExaminerAllocationChangedEmailNotificationMessage
              examinerNames={[
                examinerChange.oldExaminerName,
                examinerChange.newExaminerName,
              ].filter((name): name is string => name !== undefined)}
            />
          </S.Notification>
        )}
        <Modal.Actions>
          <Link to={generatePath(speakingSessionDetails.absolutePath, { sessionId })}>
            <Button compact type="reset" secondary>
              Cancel
            </Button>
          </Link>

          <Button
            compact
            color="green"
            disabled={!selectedExaminerId || isSaving}
            loading={isSaving}
            onClick={() =>
              assignExaminerToStructure({
                speakingSessionId: sessionId,
                speakingSessionRoomId: routeState.sessionRoomId!,
                speakingSessionRoomStructureId: routeState.sessionRoomStructureId!,
                request: { examinerId: selectedExaminerId! },
              })
            }
          >
            Confirm
          </Button>
        </Modal.Actions>
      </Modal>
    </ErrorsContextProvider>
  );
};

const S = {
  FilterSticky: styled.div`
    position: sticky;
    top: -1.5rem;
    z-index: 3;
    background: #fff;
  `,
  Notification: styled.div`
    padding: 1rem;
    background: #fff;
  `,
};
